// Repeated visits to the same listing share an attempt until an outcome or 24h expiry.
// A missing outcome means no observed completion, not proof that a buyer abandoned.
export const CONTACT_ATTEMPT_WINDOW_MS = 24 * 60 * 60 * 1000;
export const CONTACT_ATTEMPT_STORAGE_KEY = 'contact_attempts_v2';

export type ContactProfileState = 'empty' | 'partial' | 'complete' | 'unknown';

interface ContactAttemptInput {
  listingId: number;
  contactPath: string;
  loggedIn: boolean;
  entryPoint: 'listing_detail' | 'direct';
}

export interface ContactAttempt {
  id: string;
  listingId: number;
  contactPath: string;
  startedAt: number;
  initialAuth: 'guest' | 'authenticated';
  entryPoint: ContactAttemptInput['entryPoint'];
  profileState: ContactProfileState;
  wizardOpened: boolean;
  sendCount: number;
  outcome?: 'sent' | 'existing';
}

type EventParams = Record<string, string | number>;
type LoginEvent =
  | 'contact_login_code_started'
  | 'contact_login_code_sent'
  | 'contact_login_code_failed'
  | 'contact_login_verify_started'
  | 'contact_login_succeeded'
  | 'contact_login_failed'
  | 'contact_login_client_failed';

export function getContactErrorType(error: unknown) {
  const status = (error as { status?: number } | null)?.status;

  if (status === 422) return 'validation';
  if (status === 401 || status === 403) return 'unauthorized';
  if (status === 429) return 'rate_limited';
  if (status && status >= 500) return 'server';
  return 'network_or_unknown';
}

export function getContactProfileState(
  profile: Record<string, unknown> | null,
  questionKeys: string[],
): ContactProfileState {
  const answered = questionKeys.filter((key) => Boolean(profile?.[key])).length;

  if (answered === 0) return 'empty';
  return answered === questionKeys.length ? 'complete' : 'partial';
}

function withoutResumeQuery(path: string) {
  const queryStart = path.indexOf('?');
  const hashStart = path.indexOf('#');
  if (queryStart === -1 || (hashStart !== -1 && hashStart < queryStart)) return path;

  const queryEnd = hashStart === -1 ? path.length : hashStart;
  const remaining = path
    .slice(queryStart + 1, queryEnd)
    .split('&')
    .filter((parameter) => parameter.split('=', 1)[0] !== 'resume');

  // Preserve the exact route, other query parameters and fragment when matching.
  return `${path.slice(0, queryStart)}${remaining.length ? `?${remaining.join('&')}` : ''}${path.slice(queryEnd)}`;
}

export function createContactAttemptTracker({
  storage,
  emit,
  now = Date.now,
  createId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`,
}: {
  storage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  emit: (name: string, params: EventParams) => void;
  now?: () => number;
  createId?: () => string;
}) {
  let attempts: ContactAttempt[] = [];
  let storageUnavailable = false;
  const pendingSends = new Map<string, Promise<unknown>>();

  function read() {
    try {
      const stored = storageUnavailable
        ? null
        : JSON.parse(storage.getItem(CONTACT_ATTEMPT_STORAGE_KEY) ?? 'null');
      if (Array.isArray(stored)) attempts = stored;
    } catch {
      // Blocked storage must never prevent contacting a seller.
    }

    attempts = attempts.filter(
      (attempt) =>
        typeof attempt?.id === 'string' &&
        Number.isFinite(attempt.listingId) &&
        Number.isFinite(attempt.startedAt) &&
        now() - attempt.startedAt >= 0 &&
        now() - attempt.startedAt < CONTACT_ATTEMPT_WINDOW_MS,
    );
    return attempts;
  }

  function write(attempt: ContactAttempt) {
    attempts = [...read().filter((item) => item.id !== attempt.id), attempt].slice(-100);
    try {
      storage.setItem(CONTACT_ATTEMPT_STORAGE_KEY, JSON.stringify(attempts));
    } catch {
      // Retain an in-memory fallback when browser storage is unavailable.
      storageUnavailable = true;
    }
  }

  function params(attempt: ContactAttempt): EventParams {
    return {
      wizard_name: 'contact-listing',
      contact_flow_version: '2',
      contact_attempt_id: attempt.id,
      listing_id: attempt.listingId,
      contact_initial_auth: attempt.initialAuth,
      contact_entry_point: attempt.entryPoint,
      contact_profile_state: attempt.profileState,
    };
  }

  function track(name: string, attempt: ContactAttempt, extra: EventParams = {}) {
    try {
      emit(name, {
        ...params(attempt),
        contact_attempt_age_seconds: Math.max(0, Math.floor((now() - attempt.startedAt) / 1000)),
        ...extra,
      });
    } catch {
      // Analytics is best effort; sending a message is not.
    }
  }

  function start(input: ContactAttemptInput) {
    const existing = read().find((item) => item.listingId === input.listingId && !item.outcome);
    if (existing) return existing;

    const attempt: ContactAttempt = {
      id: createId(),
      listingId: input.listingId,
      contactPath: input.contactPath,
      startedAt: now(),
      initialAuth: input.loggedIn ? 'authenticated' : 'guest',
      entryPoint: input.entryPoint,
      profileState: 'unknown',
      wizardOpened: false,
      sendCount: 0,
    };
    write(attempt);
    track('contact_attempt_start', attempt);
    return attempt;
  }

  function openWizard(input: ContactAttemptInput, profileState: ContactProfileState) {
    const attempt = start(input);
    if (attempt.profileState === 'unknown') attempt.profileState = profileState;
    if (!attempt.wizardOpened) {
      attempt.wizardOpened = true;
      write(attempt);
      track('contact_wizard_open', attempt);
    } else {
      write(attempt);
    }
    return attempt;
  }

  function getForRedirect(redirect: string | null | undefined) {
    if (!redirect) return undefined;
    const contactPath = withoutResumeQuery(redirect);
    return read().find(
      (item) =>
        typeof item.contactPath === 'string' &&
        withoutResumeQuery(item.contactPath) === contactPath &&
        !item.outcome,
    );
  }

  function trackLogin(
    name: LoginEvent,
    redirect: string | null | undefined,
    extra: EventParams = {},
  ) {
    const attempt = getForRedirect(redirect);
    if (attempt) track(name, attempt, extra);
  }

  function existingConversation(listingId: number) {
    const attempt = read().find((item) => item.listingId === listingId && !item.outcome);
    if (!attempt) return;

    attempt.outcome = 'existing';
    write(attempt);
    track('contact_existing_conversation', attempt);
  }

  function send<T>(
    attempt: ContactAttempt,
    request: () => Promise<T>,
    accountScope = '',
  ): Promise<T> {
    const pendingKey = JSON.stringify([attempt.id, accountScope]);
    const pending = pendingSends.get(pendingKey);
    if (pending) return pending as Promise<T>;

    attempt.sendCount += 1;
    write(attempt);
    const sendParams = { contact_send_number: attempt.sendCount };
    track('contact_send_started', attempt, sendParams);

    const result = Promise.resolve()
      .then(request)
      .then(
        (response) => {
          attempt.outcome = 'sent';
          write(attempt);
          track('contact_send_succeeded', attempt, sendParams);
          return response;
        },
        (error) => {
          track('contact_send_failed', attempt, {
            ...sendParams,
            contact_error_type: getContactErrorType(error),
          });
          throw error;
        },
      )
      .finally(() => pendingSends.delete(pendingKey));
    pendingSends.set(pendingKey, result);
    return result;
  }

  return { start, openWizard, params, getForRedirect, trackLogin, existingConversation, send };
}
